import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { Sparkles, Save, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAISettings() {
  const { authFetch } = useAdmin();
  const [form, setForm] = useState({ prompt: '', provider: 'openai', model: 'gpt-5.2', web_enrichment_enabled: false });
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [r1, r2] = await Promise.all([
      authFetch(`${API}/admin/feeds/ai-settings`),
      authFetch(`${API}/admin/feeds/llm-providers`),
    ]);
    const d1 = await r1.json();
    const d2 = await r2.json();
    setForm(d1);
    setProviders(d2.text || []);
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    const r = await authFetch(`${API}/admin/feeds/ai-settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    setSaving(false);
    if (r.ok) toast.success('AI postavke spremljene');
    else toast.error('Greška');
  }

  if (loading) return <div className="p-10 text-center text-zinc-500"><Loader2 className="inline animate-spin mr-2" /> Učitavanje…</div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="admin-ai-settings-page">
      <header className="mb-6">
        <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><Sparkles size={28} className="text-red-500" /> AI postavke</h1>
        <p className="text-xs text-zinc-500 mt-1">Globalni AI prompt za obradu članaka iz RSS/Web feedova.</p>
      </header>

      <div className="space-y-4 bg-zinc-900/40 border border-zinc-800 rounded-xl p-6">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs text-zinc-400 block mb-1">Provajder / model</span>
            <select value={`${form.provider}/${form.model}`}
                    onChange={e => { const [p, m] = e.target.value.split('/'); setForm(f => ({ ...f, provider: p, model: m })); }}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
                    data-testid="ai-model-select">
              {providers.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-6">
            <input type="checkbox" checked={form.web_enrichment_enabled}
                   onChange={e => setForm(f => ({ ...f, web_enrichment_enabled: e.target.checked }))}
                   data-testid="ai-web-enrichment" />
            Omogući web enrichment (DuckDuckGo)
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-zinc-400 block mb-1">Globalni AI prompt</span>
          <textarea rows={18} value={form.prompt} onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono text-zinc-100 focus:border-red-500 outline-none"
                    data-testid="ai-prompt-textarea" />
        </label>
        <button onClick={save} disabled={saving}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="ai-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Spremi
        </button>
      </div>
    </div>
  );
}
